import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useOrg } from '../../contexts/OrgContext';
import { useAuth } from '../../contexts/AuthContext';
import {
  getInspection,
  startInspection,
  submitInspection,
  approveInspection,
  rejectInspection,
  cancelInspection,
  rescheduleInspection,
  Inspection,
} from '../../services/inspectionService';
import { getTemplate } from '../../services/templateService';
import { downloadInspectionReport } from '../../services/reportService';
import { FormTemplate } from '@easydone/shared';
import { FormRenderer, FormData } from '../../components/FormRenderer';
import {
  ArrowLeftIcon,
  PlayIcon,
  CheckCircleIcon,
  DocumentArrowDownIcon,
  HandThumbUpIcon,
  HandThumbDownIcon,
  XMarkIcon,
  CalendarIcon,
  WrenchScrewdriverIcon,
} from '@heroicons/react/24/outline';

export function InspectionDetailPage() {
  const { inspectionId } = useParams<{ inspectionId: string }>();
  const { currentOrg } = useOrg();
  const { currentUser } = useAuth();
  const navigate = useNavigate();

  const [inspection, setInspection] = useState<Inspection | null>(null);
  const [template, setTemplate] = useState<FormTemplate | null>(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [showRejectModal, setShowRejectModal] = useState(false);
  const [rejectionReason, setRejectionReason] = useState('');
  const [showCancelModal, setShowCancelModal] = useState(false);
  const [cancellationReason, setCancellationReason] = useState('');
  const [showRescheduleModal, setShowRescheduleModal] = useState(false);
  const [rescheduleDate, setRescheduleDate] = useState('');
  const [rescheduleReason, setRescheduleReason] = useState('');

  // Display values (either from inspection or loaded from IDs)
  const [displayFlocName, setDisplayFlocName] = useState<string | undefined>();
  const [displayFlocCode, setDisplayFlocCode] = useState<string | undefined>();
  const [displayAssetName, setDisplayAssetName] = useState<string | undefined>();
  const [displayAssetTag, setDisplayAssetTag] = useState<string | undefined>();

  useEffect(() => {
    loadInspection();
  }, [inspectionId, currentOrg]);

  // Load FLOC/Asset details if denormalized fields are missing
  useEffect(() => {
    const loadMissingDetails = async () => {
      if (!inspection || !currentOrg) return;

      // Load FLOC details if missing
      if (inspection.flocId && !inspection.flocName) {
        try {
          const { getFloc } = await import('../../services/flocService');
          const floc = await getFloc(currentOrg.id, inspection.flocId);
          if (floc) {
            setDisplayFlocName(floc.name);
            setDisplayFlocCode(floc.code);
          }
        } catch (error) {
          console.error('Failed to load FLOC details:', error);
        }
      } else {
        setDisplayFlocName(inspection.flocName);
        setDisplayFlocCode(inspection.flocCode);
      }

      // Load Asset details if missing
      if (inspection.assetId && !inspection.assetName) {
        try {
          const { getAsset } = await import('../../services/assetService');
          const asset = await getAsset(currentOrg.id, inspection.assetId);
          if (asset) {
            setDisplayAssetName(asset.name);
            setDisplayAssetTag(asset.assetId); // assetId is the user-defined asset tag/code
          }
        } catch (error) {
          console.error('Failed to load Asset details:', error);
        }
      } else {
        setDisplayAssetName(inspection.assetName);
        setDisplayAssetTag(inspection.assetTag);
      }
    };

    loadMissingDetails();
  }, [inspection, currentOrg]);

  const loadInspection = async () => {
    if (!currentOrg || !inspectionId) return;

    setLoading(true);
    try {
      const inspectionData = await getInspection(currentOrg.id, inspectionId);
      if (!inspectionData) {
        navigate('/inspections');
        return;
      }

      setInspection(inspectionData);

      // Load the template
      const templateData = await getTemplate(currentOrg.id, inspectionData.templateId);
      if (templateData) {
        setTemplate(templateData);
      }
    } catch (error) {
      console.error('Failed to load inspection:', error);
      navigate('/inspections');
    } finally {
      setLoading(false);
    }
  };

  const handleStart = async () => {
    if (!currentOrg || !inspectionId) return;

    try {
      await startInspection(currentOrg.id, inspectionId);
      await loadInspection();
    } catch (error) {
      console.error('Failed to start inspection:', error);
      alert('Failed to start inspection');
    }
  };

  const handleSubmit = async (formData: FormData) => {
    if (!currentOrg || !inspectionId) return;

    setSubmitting(true);
    try {
      await submitInspection(currentOrg.id, inspectionId, formData);
      navigate('/inspections');
    } catch (error) {
      console.error('Failed to submit inspection:', error);
      alert('Failed to submit inspection');
    } finally {
      setSubmitting(false);
    }
  };

  const handleExportPDF = async () => {
    if (!inspection || !template || !currentOrg) return;

    setExporting(true);
    try {
      await downloadInspectionReport({
        inspection,
        template,
        orgName: currentOrg.name,
      });
    } catch (error) {
      console.error('Failed to export PDF:', error);
      alert('Failed to export PDF');
    } finally {
      setExporting(false);
    }
  };

  const handleApprove = async () => {
    if (!currentOrg || !inspectionId || !currentUser) return;
    if (!confirm('Are you sure you want to approve this inspection?')) return;

    try {
      await approveInspection(
        currentOrg.id,
        inspectionId,
        currentUser.uid,
        currentUser.displayName || currentUser.email || 'User'
      );
      await loadInspection();
      alert('Inspection approved successfully');
    } catch (error) {
      console.error('Failed to approve inspection:', error);
      alert('Failed to approve inspection');
    }
  };

  const handleReject = async () => {
    if (!currentOrg || !inspectionId || !currentUser || !rejectionReason.trim()) {
      alert('Please provide a reason for rejection');
      return;
    }

    try {
      await rejectInspection(
        currentOrg.id,
        inspectionId,
        currentUser.uid,
        currentUser.displayName || currentUser.email || 'User',
        rejectionReason
      );
      setShowRejectModal(false);
      setRejectionReason('');
      await loadInspection();
      alert('Inspection rejected');
    } catch (error) {
      console.error('Failed to reject inspection:', error);
      alert('Failed to reject inspection');
    }
  };

  const handleCancel = async () => {
    if (!currentOrg || !inspectionId || !currentUser || !cancellationReason.trim()) {
      alert('Please provide a reason for cancellation');
      return;
    }

    try {
      await cancelInspection(
        currentOrg.id,
        inspectionId,
        currentUser.uid,
        currentUser.displayName || currentUser.email || 'User',
        cancellationReason
      );
      setShowCancelModal(false);
      setCancellationReason('');
      await loadInspection();
      alert('Inspection canceled');
    } catch (error) {
      console.error('Failed to cancel inspection:', error);
      alert('Failed to cancel inspection');
    }
  };

  const handleReschedule = async () => {
    if (!currentOrg || !inspectionId || !currentUser || !rescheduleDate || !rescheduleReason.trim()) {
      alert('Please provide a new date and reason for rescheduling');
      return;
    }

    try {
      await rescheduleInspection(
        currentOrg.id,
        inspectionId,
        currentUser.uid,
        currentUser.displayName || currentUser.email || 'User',
        new Date(rescheduleDate),
        rescheduleReason
      );
      setShowRescheduleModal(false);
      setRescheduleDate('');
      setRescheduleReason('');
      await loadInspection();
      alert('Inspection rescheduled successfully');
    } catch (error) {
      console.error('Failed to reschedule inspection:', error);
      alert('Failed to reschedule inspection');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-gray-500">Loading inspection...</div>
      </div>
    );
  }

  if (!inspection || !template) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-gray-500">Inspection not found</div>
      </div>
    );
  }

  const canEdit =
    inspection.status === 'scheduled' ||
    inspection.status === 'in_progress';

  const isReadOnly = !canEdit || submitting;

  return (
    <div className="max-w-5xl mx-auto">
      {/* Header */}
      <div className="mb-6">
        <button
          onClick={() => navigate('/inspections')}
          className="flex items-center text-gray-600 hover:text-gray-900 mb-4"
        >
          <ArrowLeftIcon className="w-5 h-5 mr-2" />
          Back to Inspections
        </button>

        <div className="flex items-start justify-between">
          <div>
            <div className="flex items-center gap-3 mb-2">
              <span
                className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium ${
                  inspection.status === 'scheduled'
                    ? 'bg-blue-100 text-blue-800'
                    : inspection.status === 'in_progress'
                    ? 'bg-yellow-100 text-yellow-800'
                    : inspection.status === 'submitted'
                    ? 'bg-indigo-100 text-indigo-800'
                    : inspection.status === 'pending_review'
                    ? 'bg-purple-100 text-purple-800'
                    : inspection.status === 'approved'
                    ? 'bg-green-100 text-green-800'
                    : inspection.status === 'rejected'
                    ? 'bg-red-100 text-red-800'
                    : inspection.status === 'canceled'
                    ? 'bg-gray-100 text-gray-800'
                    : inspection.status === 'reviewed'
                    ? 'bg-green-100 text-green-800'
                    : 'bg-gray-100 text-gray-800'
                }`}
              >
                {inspection.status.replace('_', ' ').toUpperCase()}
              </span>
              <h1 className="text-2xl font-bold text-gray-900">
                {inspection.templateName}
              </h1>
            </div>
            <div className="grid grid-cols-2 gap-4 text-sm text-gray-600">
              {displayFlocName && (
                <div>
                  <span className="font-medium">FLOC:</span> {displayFlocName}
                  {displayFlocCode && <span className="text-gray-500"> ({displayFlocCode})</span>}
                </div>
              )}
              {displayAssetName && (
                <div>
                  <span className="font-medium">Asset:</span> {displayAssetName}
                  {displayAssetTag && <span className="text-gray-500"> ({displayAssetTag})</span>}
                </div>
              )}
              <div>
                <span className="font-medium">Site:</span> {inspection.siteName}
              </div>
              <div>
                <span className="font-medium">Inspector:</span> {inspection.inspectorName}
              </div>
              <div>
                <span className="font-medium">Scheduled:</span>{' '}
                {inspection.scheduledDate &&
                  new Date((inspection.scheduledDate as any).seconds * 1000).toLocaleDateString()}
              </div>
              <div>
                <span className="font-medium">Created:</span>{' '}
                {inspection.createdAt &&
                  new Date((inspection.createdAt as any).seconds * 1000).toLocaleDateString()}
              </div>
            </div>
          </div>

          <div className="flex items-center gap-3">
            {inspection.status === 'scheduled' && (
              <>
                <button
                  onClick={handleStart}
                  className="btn btn-primary flex items-center"
                >
                  <PlayIcon className="w-5 h-5 mr-2" />
                  Start Inspection
                </button>
                <button
                  onClick={() => setShowRescheduleModal(true)}
                  className="btn btn-secondary flex items-center"
                >
                  <CalendarIcon className="w-5 h-5 mr-2" />
                  Reschedule
                </button>
                <button
                  onClick={() => setShowCancelModal(true)}
                  className="btn btn-secondary flex items-center text-gray-600"
                >
                  <XMarkIcon className="w-5 h-5 mr-2" />
                  Cancel
                </button>
              </>
            )}

            {inspection.status === 'in_progress' && (
              <>
                <button
                  onClick={() => setShowRescheduleModal(true)}
                  className="btn btn-secondary flex items-center"
                >
                  <CalendarIcon className="w-5 h-5 mr-2" />
                  Reschedule
                </button>
                <button
                  onClick={() => setShowCancelModal(true)}
                  className="btn btn-secondary flex items-center text-gray-600"
                >
                  <XMarkIcon className="w-5 h-5 mr-2" />
                  Cancel
                </button>
              </>
            )}

            {inspection.status === 'submitted' && (
              <div className="flex items-center gap-2 text-sm text-green-600">
                <CheckCircleIcon className="w-5 h-5" />
                Submitted for review
              </div>
            )}

            {/* Approve/Reject Buttons - Show for pending_review status */}
            {inspection.status === 'pending_review' && (
              <>
                <button
                  onClick={handleApprove}
                  className="btn btn-primary flex items-center"
                >
                  <HandThumbUpIcon className="w-5 h-5 mr-2" />
                  Approve
                </button>
                <button
                  onClick={() => setShowRejectModal(true)}
                  className="btn btn-secondary flex items-center text-red-600 border-red-300 hover:bg-red-50"
                >
                  <HandThumbDownIcon className="w-5 h-5 mr-2" />
                  Reject
                </button>
              </>
            )}

            {/* Export PDF Button - Show for completed/submitted/approved inspections */}
            {(inspection.status === 'submitted' ||
              inspection.status === 'reviewed' ||
              inspection.status === 'completed' ||
              inspection.status === 'approved' ||
              inspection.status === 'rejected') && (
              <button
                onClick={handleExportPDF}
                disabled={exporting}
                className="btn btn-secondary flex items-center"
              >
                <DocumentArrowDownIcon className="w-5 h-5 mr-2" />
                {exporting ? 'Generating...' : 'Export PDF'}
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Maintenance Alert */}
      {inspection.maintenanceRequired && (
        <div className="card bg-red-50 border-red-200 mb-6">
          <div className="flex items-start gap-3">
            <WrenchScrewdriverIcon className="w-6 h-6 text-red-600 flex-shrink-0 mt-0.5" />
            <div className="flex-1">
              <h3 className="text-lg font-semibold text-red-900 mb-1">
                Maintenance Required
              </h3>
              <div className="space-y-2 text-sm">
                <div>
                  <span className="font-medium text-red-900">Urgency:</span>{' '}
                  <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                    inspection.maintenanceUrgency === 'critical' ? 'bg-red-600 text-white' :
                    inspection.maintenanceUrgency === 'high' ? 'bg-orange-600 text-white' :
                    inspection.maintenanceUrgency === 'medium' ? 'bg-yellow-600 text-white' :
                    'bg-blue-600 text-white'
                  }`}>
                    {inspection.maintenanceUrgency?.toUpperCase()}
                  </span>
                </div>
                {inspection.maintenanceNotes && (
                  <div>
                    <span className="font-medium text-red-900">Notes:</span>{' '}
                    <span className="text-gray-700">{inspection.maintenanceNotes}</span>
                  </div>
                )}
                {inspection.maintenanceReportedBy && (
                  <div className="text-gray-600">
                    Reported by {inspection.maintenanceReportedBy}
                    {inspection.maintenanceReportedAt && (
                      <span> on {new Date((inspection.maintenanceReportedAt as any).seconds * 1000).toLocaleDateString()}</span>
                    )}
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Inspection Context Banner - Sticky during form filling */}
      {inspection.status !== 'scheduled' && (
        <div className="sticky top-0 z-10 bg-blue-50 border-l-4 border-blue-500 p-4 mb-6 shadow-sm">
          <div className="flex items-center justify-between">
            <div>
              <h3 className="text-sm font-semibold text-blue-900 mb-1">
                Inspecting:
              </h3>
              <div className="flex gap-4 text-sm text-blue-800">
                {displayFlocName && (
                  <div>
                    <span className="font-medium">FLOC:</span> {displayFlocName}
                    {displayFlocCode && <span className="text-blue-600"> ({displayFlocCode})</span>}
                  </div>
                )}
                {displayAssetName && (
                  <div>
                    <span className="font-medium">Asset:</span> {displayAssetName}
                    {displayAssetTag && <span className="text-blue-600"> ({displayAssetTag})</span>}
                  </div>
                )}
                {!displayFlocName && !displayAssetName && (
                  <div className="text-blue-600">
                    Site: {inspection.siteName}
                  </div>
                )}
              </div>
            </div>
            <div className="text-xs text-blue-700">
              {inspection.templateName}
            </div>
          </div>
        </div>
      )}

      {/* Form Renderer */}
      {inspection.status === 'scheduled' ? (
        <div className="card text-center py-12">
          <PlayIcon className="w-12 h-12 text-gray-400 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-gray-900 mb-2">
            Inspection not started
          </h3>
          <p className="text-gray-600 mb-4">
            Click "Start Inspection" to begin filling out the form
          </p>
        </div>
      ) : (
        <FormRenderer
          template={template}
          initialData={inspection.formData}
          onSubmit={handleSubmit}
          readOnly={isReadOnly}
          assetId={inspection.assetId}
          flocId={inspection.flocId}
          orgId={currentOrg?.id}
        />
      )}

      {/* Rejection Modal */}
      {showRejectModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg p-6 max-w-md w-full">
            <h2 className="text-xl font-bold mb-4">Reject Inspection</h2>
            <p className="text-gray-600 mb-4">
              Please provide a reason for rejecting this inspection:
            </p>
            <textarea
              value={rejectionReason}
              onChange={(e) => setRejectionReason(e.target.value)}
              className="input w-full"
              rows={4}
              placeholder="Enter rejection reason..."
              autoFocus
            />
            <div className="flex justify-end space-x-3 mt-6">
              <button
                type="button"
                onClick={() => {
                  setShowRejectModal(false);
                  setRejectionReason('');
                }}
                className="btn btn-secondary"
              >
                Cancel
              </button>
              <button
                onClick={handleReject}
                disabled={!rejectionReason.trim()}
                className="btn btn-primary bg-red-600 hover:bg-red-700 disabled:opacity-50"
              >
                Reject Inspection
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Cancel Modal */}
      {showCancelModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg p-6 max-w-md w-full">
            <h2 className="text-xl font-bold mb-4">Cancel Inspection</h2>
            <p className="text-gray-600 mb-4">
              Please provide a reason for canceling this inspection:
            </p>
            <textarea
              value={cancellationReason}
              onChange={(e) => setCancellationReason(e.target.value)}
              className="input w-full"
              rows={4}
              placeholder="e.g., Incorrectly assigned, Equipment not available..."
              autoFocus
            />
            <div className="flex justify-end space-x-3 mt-6">
              <button
                type="button"
                onClick={() => {
                  setShowCancelModal(false);
                  setCancellationReason('');
                }}
                className="btn btn-secondary"
              >
                Back
              </button>
              <button
                onClick={handleCancel}
                disabled={!cancellationReason.trim()}
                className="btn btn-primary bg-gray-600 hover:bg-gray-700 disabled:opacity-50"
              >
                Cancel Inspection
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Reschedule Modal */}
      {showRescheduleModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg p-6 max-w-md w-full">
            <h2 className="text-xl font-bold mb-4">Reschedule Inspection</h2>
            <p className="text-gray-600 mb-4">
              Select a new date and provide a reason for rescheduling:
            </p>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  New Scheduled Date *
                </label>
                <input
                  type="date"
                  value={rescheduleDate}
                  onChange={(e) => setRescheduleDate(e.target.value)}
                  className="input w-full"
                  min={new Date().toISOString().split('T')[0]}
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Reason *
                </label>
                <textarea
                  value={rescheduleReason}
                  onChange={(e) => setRescheduleReason(e.target.value)}
                  className="input w-full"
                  rows={3}
                  placeholder="e.g., Equipment outage, Plant unavailable..."
                  required
                />
              </div>
            </div>
            <div className="flex justify-end space-x-3 mt-6">
              <button
                type="button"
                onClick={() => {
                  setShowRescheduleModal(false);
                  setRescheduleDate('');
                  setRescheduleReason('');
                }}
                className="btn btn-secondary"
              >
                Cancel
              </button>
              <button
                onClick={handleReschedule}
                disabled={!rescheduleDate || !rescheduleReason.trim()}
                className="btn btn-primary disabled:opacity-50"
              >
                Reschedule Inspection
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
